import React from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './DeliveryRouteMap.css';

const makeIcon = (color, label) => L.divIcon({
    className: 'drm-pin',
    html: `<div class="drm-pin-inner" style="background:${color}"><span>${label}</span></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
});
const pickupIcon = makeIcon('#f59e0b', 'R'); // Restoran (alım)
const dropIcon = makeIcon('#10b981', 'M');   // Müşteri (teslim)

// Marker + rota noktalarına otomatik zoom/pan.
// Not: Harita scroll'lu drawer içinde mount olur; ilk render'da container boyutu
// yanlış olabilir → fitBounds fazla zoom yapıp tek pini gösterir (drop ekran dışı).
// invalidateSize + layout oturunca refit ile iki nokta da kadraja girer.
function FitBounds({ points }) {
    const map = useMap();
    React.useEffect(() => {
        if (!points || points.length === 0) return;
        const fit = () => {
            map.invalidateSize();
            if (points.length === 1) { map.setView(points[0], 15); return; }
            map.fitBounds(points, { padding: [40, 40], maxZoom: 16 });
        };
        const t0 = setTimeout(fit, 0);      // ilk frame
        const t1 = setTimeout(fit, 250);    // drawer animasyonu/layout otursun
        const t2 = setTimeout(fit, 600);    // yavaş tile/layout için son güvence
        return () => { clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); };
    }, [map, points]);
    return null;
}

const fmtKm = (m) => (m == null ? null : (m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1)} km`));
const fmtCoord = (p) => (p && p[0] != null && p[1] != null ? `${Number(p[0]).toFixed(5)}, ${Number(p[1]).toFixed(5)}` : null);

const DeliveryRouteMap = ({ pickup, drop, route, distanceMeters }) => {
    const hasPickup = pickup && pickup[0] != null && pickup[1] != null;
    const hasDrop = drop && drop[0] != null && drop[1] != null;
    if (!hasPickup && !hasDrop) return null;

    const hasRoute = Array.isArray(route) && route.length > 1;
    // Gerçek yol rotası varsa onu, yoksa iki nokta arası düz çizgi (kesikli) çiz.
    const line = hasRoute ? route : [hasPickup && pickup, hasDrop && drop].filter(Boolean);
    const bounds = [hasPickup && pickup, hasDrop && drop, ...(hasRoute ? route : [])].filter(Boolean);
    const center = bounds[0] || [41.0082, 28.9784];

    return (
        <div className="drm">
            {distanceMeters != null && (
                <div className="drm-distance">Toplam mesafe: <strong>{fmtKm(distanceMeters)}</strong></div>
            )}
            <MapContainer center={center} zoom={13} className="drm-map" scrollWheelZoom={false}>
                <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {hasPickup && (
                    <Marker position={pickup} icon={pickupIcon}>
                        <Tooltip direction="top" offset={[0, -12]} permanent className="drm-tip drm-tip-pickup">
                            <span className="drm-tip-title">Teslim Alma</span>
                            <span className="drm-tip-coord">{fmtCoord(pickup)}</span>
                        </Tooltip>
                    </Marker>
                )}
                {hasDrop && (
                    <Marker position={drop} icon={dropIcon}>
                        <Tooltip direction="bottom" offset={[0, 12]} permanent className="drm-tip drm-tip-drop">
                            <span className="drm-tip-title">Teslim Noktası</span>
                            <span className="drm-tip-coord">{fmtCoord(drop)}</span>
                        </Tooltip>
                    </Marker>
                )}
                {line.length > 1 && (
                    <Polyline
                        positions={line}
                        pathOptions={{ color: '#6366f1', weight: 4, opacity: 0.85, dashArray: hasRoute ? null : '8 8' }}
                    />
                )}
                <FitBounds points={bounds} />
            </MapContainer>
            <div className="drm-legend">
                <span className="drm-leg"><span className="drm-dot" style={{ background: '#f59e0b' }} /> Teslim Alma</span>
                <span className="drm-leg"><span className="drm-dot" style={{ background: '#10b981' }} /> Teslim Noktası</span>
                {!hasRoute && <span className="drm-leg-note">kuş uçuşu (yol rotası alınamadı)</span>}
            </div>
        </div>
    );
};

export default DeliveryRouteMap;
